// risk-scoring.js - Tier 2 Data & Research
export const content = {
  heroStyle: 'default',
  tag: 'Analytics',

  html: `
<p>If you had to choose between calling a random client and calling the client most likely to leave, which would you pick? The answer is obvious—but most agents make that random choice every day because they don't have a way to identify who's at risk.</p>

<p>Risk scoring changes that. It's a systematic way to calculate churn probability for every client in your book, so you can prioritize your time where it matters most.</p>

<h2>What Is a Risk Score?</h2>

<p>A risk score is a number (usually 0-100) that represents the probability a client will leave. Higher score = higher risk. The score is calculated by combining multiple factors known to predict churn.</p>

<p><strong>Example:</strong></p>
<ul>
<li>Client A: Risk score 75 (high priority)</li>
<li>Client B: Risk score 45 (moderate priority)</li>
<li>Client C: Risk score 15 (low priority)</li>
</ul>

<p>When you have limited time, work the list from top to bottom. The math says Client A is most likely to leave—that's where your attention should go first.</p>

<h2>Risk Factor Components</h2>

<p>A good risk score incorporates multiple factor categories:</p>

<h3>Engagement Factors</h3>
<ul>
<li><strong>Contact recency:</strong> Days since last meaningful interaction</li>
<li><strong>Response rate:</strong> Do they return your calls/messages?</li>
<li><strong>Initiated contact:</strong> Do they reach out to you, or only respond?</li>
<li><strong>Meeting attendance:</strong> Did they show up for scheduled reviews?</li>
</ul>

<h3>Client Lifecycle Factors</h3>
<ul>
<li><strong>Tenure:</strong> First-year clients are highest risk</li>
<li><strong>Enrollment source:</strong> AEP, SEP, OEP enrollment patterns</li>
<li><strong>Previous switching:</strong> Clients who switched to you may switch again</li>
<li><strong>Relationship depth:</strong> Multiple products = lower risk</li>
</ul>

<h3>Plan Factors</h3>
<ul>
<li><strong>Plan changes:</strong> Upcoming or recent changes to benefits, network, formulary</li>
<li><strong>Premium increase:</strong> Significant rate increase pending</li>
<li><strong>Star rating:</strong> Sub-3-star plans have year-round SEP eligibility</li>
<li><strong>Plan stability:</strong> History of volatile changes</li>
</ul>

<h3>Service Factors</h3>
<ul>
<li><strong>Open issues:</strong> Unresolved problems</li>
<li><strong>Issue history:</strong> Multiple past problems</li>
<li><strong>Satisfaction signals:</strong> Complaints, frustration expressed</li>
<li><strong>Coverage utilization:</strong> High utilization = more friction opportunities</li>
</ul>

<h3>Population Factors</h3>
<ul>
<li><strong>Dual eligibility:</strong> Quarterly SEP vulnerability</li>
<li><strong>SNP status:</strong> Different retention dynamics</li>
<li><strong>Health status:</strong> Higher needs = more plan sensitivity</li>
</ul>

<h3>External Factors</h3>
<ul>
<li><strong>Seasonal risk:</strong> Pre-AEP elevated risk</li>
<li><strong>Market conditions:</strong> Competitor activity in their area</li>
<li><strong>Life events:</strong> Known changes (move, spouse death, etc.)</li>
</ul>

<h2>Weighting and Calculation</h2>

<p>Not all factors are equally predictive. Weighting assigns more importance to stronger predictors:</p>

<p><strong>High weight factors:</strong></p>
<ul>
<li>First-year status (+15-20 points)</li>
<li>No contact in 6+ months (+15 points)</li>
<li>Significant plan change (+10-15 points)</li>
<li>Unresolved issue (+10-15 points)</li>
</ul>

<p><strong>Medium weight factors:</strong></p>
<ul>
<li>Premium increase (+5-10 points)</li>
<li>Low Star rating (+5-10 points)</li>
<li>Dual eligibility (+5 points)</li>
<li>Single product relationship (+5 points)</li>
</ul>

<p><strong>Lower weight factors:</strong></p>
<ul>
<li>Moderate contact gap (+3-5 points)</li>
<li>Minor plan changes (+3-5 points)</li>
<li>Response delay pattern (+3 points)</li>
</ul>

<p>Calculation: Add weighted points for all applicable factors. Normalize to 0-100 scale.</p>

<h2>Risk Score Tiers</h2>

<p>Translate scores into actionable tiers:</p>

<h3>Critical (80-100)</h3>
<ul>
<li>Immediate intervention required</li>
<li>Personal outreach today</li>
<li>Multiple high-weight factors present</li>
<li>Example: First-year client with plan change and no recent contact</li>
</ul>

<h3>High (60-79)</h3>
<ul>
<li>Priority outreach within 1 week</li>
<li>Schedule review conversation</li>
<li>Address specific risk factors</li>
<li>Example: Established client with significant premium increase</li>
</ul>

<h3>Moderate (40-59)</h3>
<ul>
<li>Scheduled outreach within 2-4 weeks</li>
<li>Standard retention workflow</li>
<li>Monitor for escalation</li>
<li>Example: Client with minor plan changes and moderate contact gap</li>
</ul>

<h3>Low (0-39)</h3>
<ul>
<li>Standard touchpoint cadence</li>
<li>Annual review schedule</li>
<li>Relationship maintenance mode</li>
<li>Example: Multi-year client with stable plan and regular contact</li>
</ul>

<h2>Using Risk Scores Effectively</h2>

<h3>Daily Prioritization</h3>
<p>Start each day with your highest-risk clients. If you only have time for 5 calls, make them to your 5 highest-scoring clients. The math says this maximizes retention impact per hour of effort.</p>

<h3>Weekly Review</h3>
<p>Review risk score changes weekly. Which clients jumped significantly? What changed? Rising scores are early warning signals.</p>

<h3>Pre-AEP Preparation</h3>
<p>Two months before AEP, review all moderate-and-above risk scores. These are your vulnerable clients. Prioritize outreach and annual reviews accordingly.</p>

<h3>Post-Intervention Tracking</h3>
<p>After you intervene with a high-risk client, track outcomes:</p>
<ul>
<li>Did they stay or leave?</li>
<li>Did the intervention address the risk factors?</li>
<li>What worked? What didn't?</li>
</ul>
<p>This feedback improves future interventions.</p>

<h2>Risk Score Pitfalls</h2>

<h3>Over-Relying on the Number</h3>
<p>Risk scores are decision support, not decisions. A score of 70 means "probably needs attention"—not "will definitely leave." Use judgment alongside the data.</p>

<h3>Ignoring Low Scores</h3>
<p>Low-risk clients still need attention. They're low risk because the relationship is working—don't neglect them into high-risk status.</p>

<h3>Static Scoring</h3>
<p>Risk factors change constantly. A score calculated quarterly misses real-time changes. The best systems recalculate continuously as new data comes in.</p>

<h3>Missing Factors</h3>
<p>No model captures everything. If clients are churning unexpectedly, you may be missing a predictive factor. Look for patterns in your false negatives.</p>

<h2>Building Your Scoring System</h2>

<p>You can implement risk scoring at different levels:</p>

<h3>Manual (Spreadsheet)</h3>
<ul>
<li>Track key factors in columns</li>
<li>Assign points based on factor presence</li>
<li>Sort by total score</li>
<li>Update monthly or quarterly</li>
</ul>

<h3>Semi-Automated (CRM with Custom Fields)</h3>
<ul>
<li>Build factor tracking into your CRM</li>
<li>Create calculated fields for scoring</li>
<li>Set alerts for threshold crossing</li>
<li>Update as you interact with clients</li>
</ul>

<h3>Fully Automated (Retention Platform)</h3>
<ul>
<li>System calculates scores automatically</li>
<li>Real-time updates</li>
<li>Automatic alerts and prioritization</li>
<li>Machine learning optimization</li>
</ul>

<p>Start where you are. Even basic manual scoring is better than no prioritization.</p>
  `,

  faqs: [
    {
      question: "What is a retention risk score?",
      answer: "A number (usually 0-100) representing the probability a client will leave. Higher score = higher risk. Calculated by combining multiple factors known to predict churn: engagement, plan changes, service issues, client lifecycle, and population factors."
    },
    {
      question: "What factors have the highest weight in risk scoring?",
      answer: "Strongest predictors: first-year status (+15-20 points), no contact in 6+ months (+15 points), significant plan changes (+10-15 points), and unresolved service issues (+10-15 points). These should be weighted heavily in any scoring model."
    },
    {
      question: "How should I use risk scores to prioritize my time?",
      answer: "Start each day with your highest-risk clients. If you can only make 5 calls, make them to your 5 highest-scoring clients. Critical (80+) needs immediate intervention. High (60-79) within a week. Moderate (40-59) within 2-4 weeks. Low (0-39) standard cadence."
    },
    {
      question: "How often should risk scores be updated?",
      answer: "Ideally, continuously as new data comes in. At minimum, monthly. Quarterly updates miss real-time changes. The best systems recalculate automatically when factors change—new plan announcement, contact logged, issue opened, etc."
    },
    {
      question: "Can I build a risk scoring system myself?",
      answer: "Yes. Start with a spreadsheet: track key factors, assign points, sort by total. Upgrade to CRM with custom fields for semi-automation. Eventually move to a retention platform with fully automated scoring. Start where you are—any prioritization beats random."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage Churn Patterns",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9586458/",
      publisher: "AJMC/PMC",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Pro IRP automatically calculates risk scores for every client in your book, updating in real-time as factors change. You see a prioritized list every time you log in—high risk at the top, with specific factors driving each score.`
};

export default content;
